import log, { Logger } from 'loglevel'

// Log Levels
// The logger has different log levels, allowing you to control the verbosity of log output. The available levels are:

// trace: Logs everything (most verbose).
// debug: Logs detailed information, typically used for debugging during development.
// info: Logs general application information.
// warn: Logs warning messages that indicate potential issues.
// error: Logs errors that have occurred in the application.
// silent: Disables all logging.

// Set log level based on environment
const setLogLevel = (): void => {
  if (process.env.NODE_ENV === 'development') {
    log.setLevel('debug')
  } else if (process.env.NODE_ENV === 'test') {
    log.setLevel('warn')
  } else {
    log.setLevel('info')
  }
}

// Custom type to include specific Console methods
interface ExtendedLogger extends Logger {
  group(...label: any[]): void
  groupEnd(): void
  dir(obj: any, options?: any): void
}
/**
 * Creates a scoped logger with optional scope name prepending.
 * You can also use additional console methods like `group` and `dir`.
 *
 * Example usage:
 *
 *  const userLog = createLogger('User')
 *  userLog.setLevel('debug')  // Set the log level for the scoped logger
 *  userLog.debug('Fetching user data')  // Output: [User] Fetching user data
 *  userLog.group('User Details Group')  // Use console.group to group logs
 *  userLog.dir({ user: 'John Doe' })    // Use console.dir to log objects
 *
 * @param {string} scope - The name of the scope (e.g., 'User', 'API') to prefix logs.
 * @param {object} options - Options for the logger.
 * @param {boolean} options.prependScope - Whether to prepend the scope to log messages (default: false).
 * @returns {Logger & Console} - A scoped logger instance with optional scope prepending and console methods.
 */
const createLogger = (scope: string, options: { prependScope?: boolean } = {}): ExtendedLogger => {
  const scopedLogger = log.getLogger(scope)
  const { prependScope = true } = options

  // Override the method factory to optionally prefix messages with the scope
  scopedLogger.methodFactory = function (methodName, logLevel, loggerName) {
    const rawMethod = log.methodFactory(methodName, logLevel, loggerName)

    return (...messages) => {
      if (this.getLevel() <= logLevel) {
        // Use 'this' to refer to the scoped logger
        if (prependScope) {
          rawMethod(`[${scope}]`, ...messages)
        } else {
          rawMethod(...messages)
        }
      }
    }
  }

  // Apply the methodFactory
  scopedLogger.setLevel(scopedLogger.getLevel()) // Apply the initial log level

  // Extend the logger with console.group and console.dir methods
  return {
    ...scopedLogger,
    group: (...label: any[]) => console.group(`[${scope}]`, ...label),
    groupEnd: () => console.groupEnd(),
    dir: (obj: any, options?: any) => console.dir(obj, options),
  }
}

// Set global log level immediately
setLogLevel()

// Export the logger and the scoped logger creator
export default log
export { createLogger }
